import "./i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchCaseStatus, fetchPortalStatus, getTenantCase, submitCase, type CaseCreated, type CaseStatus, type PortalBranding, type PortalStatus, type SpeedtestResult, type TenantCaseResponse } from "./api/portal";
import { BrandedFooter } from "./components/BrandedFooter";
import { FaqSection } from "./components/FaqSection";
import { FallbackForm, type CaseSubmission, type FallbackFormData } from "./components/FallbackForm";
import { DeviceConfirmPage } from "./components/DeviceConfirmPage";
import { LinkRequestedPage } from "./components/LinkRequestedPage";
import { NoWifiHelpPage } from "./components/NoWifiHelpPage";
import { QuestionnaireForm } from "./components/QuestionnaireForm";
import { SelfHelpPage } from "./components/SelfHelpPage";
import { SpeedtestPage } from "./components/SpeedtestPage";
import { TicketViewPage } from "./components/TicketViewPage";
import { TokenExpiredPage } from "./components/TokenExpiredPage";
import { WifiCheckPage } from "./components/WifiCheckPage";
import { BrandingContext, DEFAULT_BRANDING } from "./context/BrandingContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}

function applyBrandingCssVars(branding: PortalBranding) {
  document.documentElement.style.setProperty("--color-primary", branding.primary_color);
}

// ---------------------------------------------------------------------------
// StatusPage
// ---------------------------------------------------------------------------

interface StatusPageProps {
  status: PortalStatus;
  onStartHelp: () => void;
  onReportDirectly: () => void;
}

function StatusPage({ status, onStartHelp, onReportDirectly }: StatusPageProps) {
  const { t } = useTranslation();
  const { logo_url, display_name, welcome_text } = status.branding;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        {logo_url && (
          <div className="flex justify-center mb-4">
            <img
              src={logo_url}
              alt={display_name}
              className="h-10 max-w-[200px] object-contain"
            />
          </div>
        )}
        <h1 className="text-xl font-semibold text-gray-800 mb-6">
          {status.site_name}
        </h1>

        {/* AUDIT-PORTAL-1/L8: Disruption banner */}
        {status.status === "disruption" && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <span className="text-amber-500 text-lg mt-0.5" aria-hidden>⚠️</span>
            <div className="flex-1">
              <p className="text-amber-700 font-semibold text-sm mb-0.5">{t("status_disruption")}</p>
              <p className="text-amber-600 text-xs mb-2">{t("status_disruption_desc")}</p>
              <button
                type="button"
                onClick={onReportDirectly}
                className="text-xs font-medium text-amber-700 underline"
              >
                {t("disruption_report_anyway")}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
          <div className="text-4xl mb-3 text-center" style={{ color: "var(--color-primary, #2563eb)" }}>📶</div>
          <p className="text-gray-800 font-semibold text-lg text-center mb-1">
            {welcome_text || t("status_help_title")}
          </p>
          <p className="text-gray-500 text-sm text-center">
            {t("status_help_desc")}
          </p>
        </div>

        <button
          type="button"
          onClick={onStartHelp}
          className="w-full py-3 px-4 text-white rounded-xl font-semibold transition-colors"
          style={{ backgroundColor: "var(--color-primary, #2563eb)" }}
        >
          {t("start_help")}
        </button>

        {status.branding.faq_items && status.branding.faq_items.length > 0 && (
          <FaqSection items={status.branding.faq_items} />
        )}

        <BrandedFooter />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorPage
// ---------------------------------------------------------------------------

function ErrorPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4 text-center">
        <p className="text-gray-500 font-semibold text-lg mb-2">
          {t("error_title")}
        </p>
        <p className="text-gray-400 text-sm">{t("error_desc")}</p>
        <footer className="mt-10 text-gray-400 text-xs">WLAN-Hilfe</footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

type AppState =
  | { phase: "loading" }
  | { phase: "error" }
  | { phase: "loaded"; data: PortalStatus }
  | { phase: "wifi_check"; data: PortalStatus }
  | { phase: "no_wifi_help"; data: PortalStatus }
  | { phase: "speedtest"; data: PortalStatus }
  | { phase: "ip_confirm"; data: PortalStatus; speedtestResult?: SpeedtestResult }
  | { phase: "selfhelp"; data: PortalStatus; speedtestResult?: SpeedtestResult }
  | { phase: "fallback_form"; data: PortalStatus; speedtestResult?: SpeedtestResult; from: "selfhelp" | "no_wifi_help" | "disruption" }
  | { phase: "questionnaire"; data: PortalStatus; fallbackData: FallbackFormData; speedtestResult?: SpeedtestResult; from: "selfhelp" | "no_wifi_help" | "disruption" }
  | { phase: "case_ready"; submission: CaseSubmission; speedtestResult?: SpeedtestResult }
  | { phase: "case_created"; result: CaseCreated; language: string }
  | { phase: "case_status"; caseStatus: CaseStatus }
  | { phase: "solved" }
  | { phase: "ticket_view"; caseData: TenantCaseResponse; token: string }
  | { phase: "token_expired" }
  | { phase: "link_requested" };

export default function App() {
  const { t } = useTranslation();
  const [state, setState] = useState<AppState>({ phase: "loading" });
  const params = new URLSearchParams(window.location.search);
  const caseToken = (params.get("case_token") ?? "").replace(/[^A-Za-z0-9_-]/g, "") || null;
  const token = (params.get("token") ?? "").replace(/[^A-Za-z0-9_-]/g, "");
  const ticketParam = (params.get("ticket") ?? "").replace(/[^A-Za-z0-9-]/g, "") || null;

  // Extract branding from current state (if available)
  const branding: PortalBranding =
    "data" in state && state.data ? state.data.branding : DEFAULT_BRANDING;

  useEffect(() => {
    // case_token has highest priority – direct ticket view via magic link
    if (caseToken) {
      getTenantCase(caseToken)
        .then((caseData) => setState({ phase: "ticket_view", caseData, token: caseToken }))
        .catch(() => setState({ phase: "token_expired" }));
      return;
    }

    if (!token) {
      setState({ phase: "error" });
      return;
    }

    // If both token and ticket are present, go straight to case status view
    if (ticketParam) {
      fetchCaseStatus(token, ticketParam)
        .then((caseStatus) => setState({ phase: "case_status", caseStatus }))
        .catch(() => setState({ phase: "error" }));
      return;
    }

    fetchPortalStatus(token)
      .then((data) => setState({ phase: "loaded", data }))
      .catch(() => setState({ phase: "error" }));
  }, [caseToken, token, ticketParam]);

  // Apply CSS variables whenever branding changes
  useEffect(() => {
    applyBrandingCssVars(branding);
  }, [branding]);

  // Auto-refresh status every 30s while on the status page
  useEffect(() => {
    if (state.phase !== "loaded") return;
    if (!token) return;
    const id = setInterval(() => {
      fetchPortalStatus(token)
        .then((data) =>
          setState((prev) =>
            prev.phase === "loaded" ? { phase: "loaded", data } : prev
          )
        )
        .catch(() => {/* silently ignore transient errors during polling */});
    }, 30_000);
    return () => clearInterval(id);
  }, [state.phase, token]);

  // Submit case automatically when phase transitions to case_ready
  useEffect(() => {
    if (state.phase !== "case_ready") return;
    const language = navigator.language.startsWith("de") ? "de" : "en";
    submitCase(token, {
      device_type: state.submission.device_type || undefined,
      apartment: state.submission.apartment || undefined,
      since: state.submission.since || undefined,
      contact_email: state.submission.email || undefined,
      description: state.submission.description,
      identification_method: state.submission.identification_method,
      speedtest_token: state.speedtestResult?.speedtest_token ?? null,
      matched_mac_hash: null,
      language,
      hp_field: state.submission.hp_field || undefined,
    })
      .then((result) => setState({ phase: "case_created", result, language }))
      .catch(() => setState({ phase: "error" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  if (state.phase === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-sm">{t("loading")}</p>
      </div>
    );
  }

  if (state.phase === "error") {
    return <ErrorPage />;
  }

  // All remaining phases share a single BrandingContext.Provider
  let content: React.ReactNode;

  if (state.phase === "solved") {
    content = (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="text-green-500 text-5xl mb-3">✓</div>
          <p className="text-green-800 font-semibold text-lg mb-2">
            {t("selfhelp_solved_title")}
          </p>
          <p className="text-green-700 text-sm mb-6">
            {t("selfhelp_solved_desc")}
          </p>
          <button
            type="button"
            onClick={() => setState({ phase: "loading" })}
            className="py-2 px-6 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            {t("back_to_status")}
          </button>
        </div>
      </div>
    );
  } else if (state.phase === "wifi_check") {
    content = (
      <WifiCheckPage
        siteName={state.data.site_name}
        onYes={() => setState({ phase: "speedtest", data: state.data })}
        onNo={() => setState({ phase: "no_wifi_help", data: state.data })}
      />
    );
  } else if (state.phase === "no_wifi_help") {
    content = (
      <NoWifiHelpPage
        siteName={state.data.site_name}
        portalToken={token}
        onConnected={() => setState({ phase: "wifi_check", data: state.data })}
        onReportProblem={() => setState({ phase: "fallback_form", data: state.data, from: "no_wifi_help" })}
      />
    );
  } else if (state.phase === "speedtest") {
    content = (
      <SpeedtestPage
        portalToken={token}
        siteName={state.data.site_name}
        onComplete={(result) =>
          setState({ phase: "ip_confirm", data: state.data, speedtestResult: result })
        }
        onSkip={() => setState({ phase: "ip_confirm", data: state.data })}
      />
    );
  } else if (state.phase === "ip_confirm") {
    content = (
      <DeviceConfirmPage
        portalToken={token}
        siteName={state.data.site_name}
        speedtestResult={state.speedtestResult}
        matchedHostname={state.speedtestResult?.matched_hostname ?? null}
        onConfirmed={(result) =>
          setState({ phase: "selfhelp", data: state.data, speedtestResult: result })
        }
        onSkip={() => setState({ phase: "selfhelp", data: state.data, speedtestResult: state.speedtestResult })}
      />
    );
  } else if (state.phase === "selfhelp") {
    content = (
      <SelfHelpPage
        siteName={state.data.site_name}
        onReportProblem={() =>
          setState({ phase: "fallback_form", data: state.data, speedtestResult: state.speedtestResult, from: "selfhelp" })
        }
      />
    );
  } else if (state.phase === "fallback_form") {
    content = (
      <FallbackForm
        speedtestResult={state.speedtestResult}
        onSubmit={(formData) => setState({ phase: "questionnaire", data: state.data, fallbackData: formData, speedtestResult: state.speedtestResult, from: state.from })}
        onBack={() => {
          if (state.from === "no_wifi_help") {
            setState({ phase: "no_wifi_help", data: state.data });
          } else if (state.from === "disruption") {
            setState({ phase: "loaded", data: state.data });
          } else {
            setState({ phase: "selfhelp", data: state.data, speedtestResult: state.speedtestResult });
          }
        }}
      />
    );
  } else if (state.phase === "questionnaire") {
    content = (
      <QuestionnaireForm
        fallbackData={state.fallbackData}
        onSubmit={(submission) => setState({ phase: "case_ready", submission, speedtestResult: state.speedtestResult })}
        onBack={() => setState({ phase: "fallback_form", data: state.data, speedtestResult: state.speedtestResult, from: state.from })}
      />
    );
  } else if (state.phase === "case_ready") {
    content = (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-sm">{t("case_submitting")}</p>
      </div>
    );
  } else if (state.phase === "case_created") {
    const statusLabels: Record<string, string> = {
      open: t("case_status_open"),
      in_progress: t("case_status_in_progress"),
      resolved: t("case_status_resolved"),
    };
    content = (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto px-4 py-8 text-center">
          <div className="text-green-500 text-5xl mb-4">&#x2713;</div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            {t("case_ready_title")}
          </h1>
          <p className="text-gray-500 text-sm mb-4">{t("case_ready_desc")}</p>
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-400 mb-1">{t("case_ticket_label")}</p>
            <p className="text-2xl font-mono font-bold text-gray-800">
              {state.result.ticket_number}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-400 mb-1">{t("case_status_label")}</p>
            <p className="text-sm font-medium text-gray-700">
              {statusLabels[state.result.status] ?? state.result.status}
            </p>
          </div>
          <p className="text-gray-400 text-xs">{t("case_email_hint")}</p>
          <BrandedFooter />
        </div>
      </div>
    );
  } else if (state.phase === "case_status") {
    const statusLabels: Record<string, string> = {
      open: t("case_status_open"),
      in_progress: t("case_status_in_progress"),
      resolved: t("case_status_resolved"),
    };
    content = (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto px-4 py-8 text-center">
          <h1 className="text-xl font-semibold text-gray-800 mb-6">
            {t("case_status_page_title")}
          </h1>
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-400 mb-1">{t("case_ticket_label")}</p>
            <p className="text-2xl font-mono font-bold text-gray-800">
              {state.caseStatus.ticket_number}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-400 mb-1">{t("case_status_label")}</p>
            <p className="text-sm font-medium text-gray-700">
              {statusLabels[state.caseStatus.status] ?? state.caseStatus.status}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-xs text-gray-400 mb-1">{t("case_last_updated")}</p>
            <p className="text-sm text-gray-700">
              {formatDate(state.caseStatus.updated_at)}
            </p>
          </div>
          <footer className="mt-10 text-gray-400 text-xs">WLAN-Hilfe</footer>
        </div>
      </div>
    );
  } else if (state.phase === "ticket_view") {
    const lang = navigator.language.startsWith("de") ? "de" : "en";
    content = (
      <TicketViewPage
        token={state.token}
        caseData={state.caseData}
        lang={lang}
      />
    );
  } else if (state.phase === "token_expired") {
    content = (
      <TokenExpiredPage
        onLinkRequested={() => setState({ phase: "link_requested" })}
      />
    );
  } else if (state.phase === "link_requested") {
    content = <LinkRequestedPage />;
  } else {
    // "loaded" phase → StatusPage
    content = (
      <StatusPage
        status={state.data}
        onStartHelp={() => setState({ phase: "wifi_check", data: state.data })}
        onReportDirectly={() => setState({ phase: "fallback_form", data: state.data, from: "disruption" })}
      />
    );
  }

  return (
    <BrandingContext.Provider value={branding}>
      {content}
    </BrandingContext.Provider>
  );
}
